import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  useMetricsUi,
  headCellSx,
  numCellSx,
  textCellSx,
  SectionHead,
} from "./metricsUi";
import { formatMetricCell, periodAvailabilityLabel } from "./metricsFormat";

/**
 * Historical fiscal-period performance (sales metrics only — never inventory).
 * Click a row to switch the selected period. Shows subtle status for
 * in-progress / upcoming / no-data periods without changing layout language.
 */
const FiscalPeriodHistoryTable = ({
  periodHistory = [],
  selectedPeriodId,
  onSelectPeriod,
  title = "Fiscal period performance",
  subtitle = "Complete fiscal year (P01–P12). Inventory is not included — current on-hand is live, not period-end stock. Select a period to load weeks and daily detail.",
  periodColumnLabel = "Fiscal Period",
}) => {
  const ui = useMetricsUi();

  if (!periodHistory.length) return null;

  // Chronological API order (P01→P12 or Jan→Dec); do not reverse.
  const rows = periodHistory;

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHead title={title} subtitle={subtitle} />

      <TableContainer sx={{ ...ui.card, overflow: "hidden" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx(ui)}>{periodColumnLabel}</TableCell>
              <TableCell sx={headCellSx(ui)}>Date range</TableCell>
              <TableCell sx={headCellSx(ui)}>Status</TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross Sales
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Orders
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Units Sold
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                UPT
              </TableCell>
              <TableCell sx={headCellSx(ui, { textAlign: "right" })}>
                Gross AOV
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const selected = row.period_id === selectedPeriodId;
              const statusLabel = periodAvailabilityLabel(
                row.availability_status,
              );
              const upcoming = row.availability_status === "upcoming";
              return (
                <TableRow
                  key={row.period_id}
                  hover
                  onClick={() =>
                    !upcoming && onSelectPeriod?.(row.period_id)
                  }
                  sx={{
                    cursor: upcoming
                      ? "default"
                      : onSelectPeriod
                        ? "pointer"
                        : "default",
                    opacity: upcoming ? 0.72 : 1,
                    backgroundColor: selected ? ui.accentSoft : "transparent",
                    "&:hover td": {
                      backgroundColor: upcoming ? "transparent" : ui.surfaceAlt,
                    },
                    "&:last-child td": { borderBottom: "none" },
                  }}
                >
                  <TableCell sx={textCellSx(ui)}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        {row.label}
                      </Typography>
                      {selected && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: ui.accent,
                            backgroundColor: ui.accentSoft,
                            borderRadius: "10px",
                            px: 0.8,
                            py: 0.1,
                            letterSpacing: "0.03em",
                          }}
                        >
                          VIEWING
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={textCellSx(ui)}>
                    <Typography
                      sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}
                    >
                      {row.start_date} – {row.end_date}
                    </Typography>
                  </TableCell>
                  <TableCell sx={textCellSx(ui)}>
                    {statusLabel ? (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10.5,
                          fontWeight: 600,
                          color:
                            row.availability_status === "in_progress"
                              ? ui.accent
                              : row.availability_status === "upcoming"
                                ? ui.colors.textTertiary
                                : ui.warning,
                          backgroundColor:
                            row.availability_status === "in_progress"
                              ? ui.accentSoft
                              : row.availability_status === "upcoming"
                                ? ui.flatBg
                                : ui.warningBg,
                          borderRadius: "10px",
                          px: 0.9,
                          py: 0.2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statusLabel}
                      </Box>
                    ) : (
                      <Typography
                        sx={{ fontSize: 11.5, color: ui.colors.textTertiary }}
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={numCellSx(ui, { fontWeight: 600 })}>
                    {upcoming
                      ? "—"
                      : formatMetricCell(row.gross_sales, "currency")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {upcoming ? "—" : formatMetricCell(row.orders, "count")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {upcoming
                      ? "—"
                      : formatMetricCell(row.units_sold, "count")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {upcoming ? "—" : formatMetricCell(row.upt, "ratio")}
                  </TableCell>
                  <TableCell sx={numCellSx(ui)}>
                    {upcoming
                      ? "—"
                      : formatMetricCell(row.gross_aov, "currency2")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FiscalPeriodHistoryTable;
